import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import theme from '../theme';
import { Card, Money, Spinner, StatusBadge, Button, Empty } from '../components/common';

export default function Dashboard({ profile }) {
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState([]);
  const [payments, setPayments] = useState(null);
  const [limits, setLimits] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get('/accounts'),
      api.get('/payments?limit=5'),
      api.get('/payment-limits'),
    ]).then(([acc, pmt, lim]) => {
      setAccounts(acc);
      setPayments(pmt);
      setLimits(lim);
    }).catch(console.error).finally(() => setLoading(false));
  }, []);

  if (loading) return <div style={{ textAlign: 'center', padding: '60px' }}><Spinner size={32} /></div>;

  return (
    <div>
      {/* Greeting */}
      <div style={{ marginBottom: '24px' }}>
        <h1 style={{ margin: '0 0 4px', fontSize: '24px', color: theme.colors.text }}>
          Welcome back, {profile?.customerName || profile?.username}
        </h1>
        <p style={{ margin: 0, color: theme.colors.textLight, fontSize: '14px' }}>
          {new Date().toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
        </p>
      </div>

      {/* Quick Actions */}
      <div style={{ display: 'flex', gap: '12px', marginBottom: '24px', flexWrap: 'wrap' }}>
        <Button variant="accent" onClick={() => navigate('/payments/new')}>Make a payment</Button>
        <Button variant="outline" onClick={() => navigate('/accounts')}>View statements</Button>
      </div>

      {/* Account Cards */}
      <h2 style={{ fontSize: '16px', marginBottom: '12px', color: theme.colors.text }}>Your accounts</h2>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '16px', marginBottom: '32px' }}>
        {accounts.filter(a => a.status === 'ACTIVE').map(acc => (
          <Card
            key={acc.id}
            onClick={() => navigate(`/accounts/${acc.id}`)}
            style={{ cursor: 'pointer', transition: 'box-shadow 0.15s' }}
          >
            <div style={{ fontSize: '13px', color: theme.colors.textLight, marginBottom: '4px' }}>{acc.accountNumber}</div>
            <div style={{ fontSize: '15px', fontWeight: 600, marginBottom: '16px', color: theme.colors.text }}>{acc.name}</div>
            <div style={{ fontSize: '12px', color: theme.colors.textMuted, marginBottom: '2px' }}>Available balance</div>
            <div style={{ fontSize: '24px', fontWeight: 700, color: theme.colors.primary }}>
              <Money amount={acc.balance?.available || 0} currency={acc.currency} />
            </div>
            <div style={{ marginTop: '8px', fontSize: '12px', color: theme.colors.textMuted }}>
              Posted: <Money amount={acc.balance?.posted || 0} currency={acc.currency} />
            </div>
          </Card>
        ))}
      </div>

      {/* Payment Limits */}
      {limits && (
        <Card style={{ marginBottom: '24px' }}>
          <h3 style={{ margin: '0 0 12px', fontSize: '14px', color: theme.colors.text }}>Payment limits</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '12px' }}>
            <div>
              <div style={{ fontSize: '12px', color: theme.colors.textMuted }}>Daily remaining</div>
              <div style={{ fontSize: '16px', fontWeight: 600 }}>
                <Money amount={limits.daily_aggregate_max - limits.daily_used} />
              </div>
              <div style={{ fontSize: '11px', color: theme.colors.textMuted }}>
                of <Money amount={limits.daily_aggregate_max} /> daily limit
              </div>
            </div>
            <div>
              <div style={{ fontSize: '12px', color: theme.colors.textMuted }}>Single payment max</div>
              <div style={{ fontSize: '16px', fontWeight: 600 }}><Money amount={limits.single_payment_max} /></div>
            </div>
          </div>
        </Card>
      )}

      {/* Recent Payments */}
      <h2 style={{ fontSize: '16px', marginBottom: '12px', color: theme.colors.text }}>Recent payments</h2>
      {payments?.data?.length > 0 ? (
        <Card style={{ padding: 0, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
            <thead>
              <tr style={{ background: theme.colors.bgAlt }}>
                <th style={thStyle}>Date</th>
                <th style={thStyle}>Reference</th>
                <th style={thStyle}>Scheme</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Amount</th>
                <th style={thStyle}>Status</th>
              </tr>
            </thead>
            <tbody>
              {payments.data.map(p => (
                <tr key={p.id} onClick={() => navigate(`/payments/${p.id}`)} style={{ cursor: 'pointer' }}>
                  <td style={tdStyle}>{new Date(p.createdAt).toLocaleDateString('en-GB')}</td>
                  <td style={tdStyle}>{p.reference}</td>
                  <td style={tdStyle}>{p.scheme}</td>
                  <td style={{ ...tdStyle, textAlign: 'right' }}><Money amount={p.amount} currency={p.currency} /></td>
                  <td style={tdStyle}><StatusBadge status={p.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
          {payments.total > 5 && (
            <div style={{ padding: '12px', textAlign: 'center', borderTop: `1px solid ${theme.colors.border}` }}>
              <Button variant="ghost" size="sm" onClick={() => navigate('/payments')}>View all payments</Button>
            </div>
          )}
        </Card>
      ) : (
        <Empty message="No payments yet" />
      )}
    </div>
  );
}

const thStyle = { padding: '10px 16px', textAlign: 'left', fontWeight: 600, fontSize: '12px', color: theme.colors.textMuted, borderBottom: `1px solid ${theme.colors.border}` };
const tdStyle = { padding: '12px 16px', borderBottom: `1px solid ${theme.colors.borderLight}` };
